import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BEMBlock } from 'helpers/classnames';
import { useModal } from 'palette/Modal';
import { useSearchFocusOnClose, useGlobalSearch } from '@common/GlobalSearch/hooks';
import { GA_SEARCH_CATEGORY, GA_SEARCH_HEADER_LABEL, GA_SEARCH_OPEN_ACTION } from '@common/Tracking';
import { useTranslate } from '@common/Translations';
import { ga4event, pushGAEventToDataLayer } from '@helpers/dataLayer';
import { toggleSearchSidebarVisibility } from '../actions';
import GlobalSearch from '../components/GlobalSearch';
import { DESKTOP_SEARCH_MODAL_ID, GA4_EVENT } from '../constants';
import { convertToLowerCase } from '../helpers';
import type { GlobalSearchResultsData } from '../types';
import '../styles/GlobalSearchContainer.scss';

interface GlobalSearchContainerProps {
    hasCloseButton: boolean;
    isHidden?: boolean;
}

function GlobalSearchContainer({ hasCloseButton, isHidden = false }: GlobalSearchContainerProps) {
    const { setIsSearchModalOpen, focusElementRef } = useSearchFocusOnClose();
    const inputRef = useRef<HTMLInputElement>(null);
    const { setActiveModalId } = useModal();
    const translate = useTranslate();
    const dispatch = useDispatch();
    const [, setSearchTerm] = useGlobalSearch();
    const searchResults = useSelector<any>(state => state.globalSearch.searchResults) as GlobalSearchResultsData;
    const searchTerm = useSelector<any>(state => state.globalSearch.searchTerm) as string;
    const isSearchSidebarOpen = useSelector<any>(state => state.globalSearch.sidebarVisibility);
    const [isScreenTouched, setIsScreenTouched] = useState(false);
    const suggestedQuery = searchResults?.metadata?.suggested_query;
    const redirectUrl = searchResults?.metadata?.redirect_url;
    const [block] = BEMBlock('global-search-container', { visibility: isHidden });

    const handleModalClose = () => {
        dispatch(toggleSearchSidebarVisibility(false));
        setActiveModalId(null);

        ga4event(GA4_EVENT.cancelSearch, {
            search_term: convertToLowerCase(searchTerm),
            search_suggested_query: suggestedQuery,
        });
    };

    const handleClick = () => {
        if (!isSearchSidebarOpen) {
            pushGAEventToDataLayer(GA_SEARCH_CATEGORY, GA_SEARCH_OPEN_ACTION, GA_SEARCH_HEADER_LABEL);
        }

        dispatch(toggleSearchSidebarVisibility(true));
        setIsSearchModalOpen(true);
    };

    const handleTouchScreen = (state: boolean) => {
        setIsScreenTouched(state);
    };

    useEffect(() => {
        if (isSearchSidebarOpen) {
            setActiveModalId(DESKTOP_SEARCH_MODAL_ID);
        } else {
            setActiveModalId(null);
        }
    }, [isSearchSidebarOpen, setActiveModalId]);

    return (
        <div className={block}>
            <GlobalSearch
                hasCloseButton={hasCloseButton}
                header={translate('global_search_header')}
                inputRef={inputRef}
                isScreenTouched={isScreenTouched}
                redirectUrl={redirectUrl}
                searchButtonRef={focusElementRef}
                searchResults={searchResults}
                searchTerm={searchTerm}
                suggestedQuery={suggestedQuery}
                onClick={handleClick}
                onModalClose={handleModalClose}
                onSearchTermChange={setSearchTerm}
                onTouchScreen={handleTouchScreen}
            />
        </div>
    );
}

export default GlobalSearchContainer;
